import axios from 'axios';
import { getAuthToken, clearAuth } from './auth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.response?.data?.detail?.includes('expired')) {
      clearAuth();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestOTP: (email) => api.post('/auth/request-otp', { email }),
  verifyOTP: (email, otp, keepLoggedIn = false) => api.post('/auth/verify-otp', { email, otp, keep_logged_in: keepLoggedIn }),
  googleCallback: (data) => api.post('/auth/google-callback', data),
  getMe: () => api.get('/auth/me'),
};

export const vehicleAPI = {
  verify: (plateNumber) => api.post('/vehicles/verify', { plate_number: plateNumber }),
  getAll: () => api.get('/vehicles'),
  getByVilla: (villaNumber) => api.get('/vehicles/by-villa', { params: { villa_number: villaNumber } }),
  create: (data) => api.post('/vehicles', data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  setEPC: (id, epc_hex) => api.patch(`/vehicles/${id}/epc`, null, { params: { epc_hex } }),
  exportCsv: () => api.get('/export/vehicles.csv', { responseType: 'blob' }),
  uploadCSV: (formData) => api.post('/vehicles/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadPicture: (id, formData) => api.post(`/vehicles/${id}/picture`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPictureUrl: (id) => `${API_URL}/vehicles/${id}/picture`,
};

export const visitorAPI = {
  getAll: (villaNumber) => api.get('/visitors', { params: { villa_number: villaNumber } }),
  pending: () => api.get('/visitors/pending'),
  create: (data) => api.post('/visitors', data),
  approve: (id) => api.patch(`/visitors/${id}/approve`),
  checkout: (id) => api.patch(`/visitors/${id}/checkout`),
};

export const gatePassAPI = {
  getAll: (villaNumber) => api.get('/gate-passes', { params: { villa_number: villaNumber } }),
  create: (data) => api.post('/gate-passes', data),
  verify: (accessCode) => api.post('/gate-passes/verify', { access_code: accessCode }),
  approve: (passId) => api.post(`/gate-passes/${passId}/approve`),
  reject: (passId) => api.post(`/gate-passes/${passId}/reject`),
  createParty: (data) => api.post('/gate-passes/party', data),
  deliveryByVilla: (villa) => api.get('/gate-passes/delivery/by-villa', { params: { villa_number: villa } }),
  guardApproveDelivery: (passId, plate) => api.post(`/gate-passes/delivery/${passId}/approve-entry`, { vehicle_plate: plate }),
};

export const noticeAPI = {
  getAll: () => api.get('/notices'),
  create: (data) => api.post('/notices', data),
  delete: (id) => api.delete(`/notices/${id}`),
};

export const analyticsAPI = {
  getTraffic: () => api.get('/analytics/traffic'),
};

export const facilityAPI = {
  getAll: () => api.get('/facilities'),
  create: (data) => api.post('/facilities', data),
  delete: (id) => api.delete(`/facilities/${id}`),
  book: (facilityId, data) => api.post(`/facilities/${facilityId}/book`, data),
  blockSlot: (facilityId, date, slot) => api.post(`/facilities/${facilityId}/block-slot`, null, { params: { date, slot } }),
  blockDates: (facilityId, startDate, endDate, reason) => api.post(`/facilities/${facilityId}/block-dates`, null, { params: { start_date: startDate, end_date: endDate, reason } }),
  unblockSlot: (facilityId, date, slot) => api.delete(`/facilities/${facilityId}/unblock-slot`, { params: { date, slot } }),
};

export const bookingAPI = {
  getPending: () => api.get('/bookings/pending'),
  approve: (bookingId) => api.patch(`/bookings/${bookingId}/approve`),
  reject: (bookingId, reason) => api.patch(`/bookings/${bookingId}/reject`, null, { params: { reason } }),
};

export const staffAPI = {
  getAll: (villaNumber) => api.get('/staff', { params: { villa_number: villaNumber } }),
  getByVilla: (villaNumber) => api.get('/staff/by-villa', { params: { villa_number: villaNumber } }),
  create: (data) => api.post('/staff', data),
  delete: (staffId) => api.delete(`/staff/${staffId}`),
  search: (idNumber) => api.get('/staff/search', { params: { id_number: idNumber } }),
  checkIn: (data) => api.post('/staff/check-in', data),
  checkOut: (entryId) => api.patch(`/staff/check-out/${entryId}`),
  getActive: (villaNumber) => api.get('/staff/active', { params: { villa_number: villaNumber } }),
  getMyStaff: () => api.get('/staff/my-staff'),
  addVilla: (staffId, villaNumber) => api.post(`/staff/${staffId}/add-villa`, { villa_number: villaNumber }),
  uploadCSV: (formData) => api.post('/staff/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const paymentAPI = {
  getAll: (villaNumber) => api.get('/payments', { params: { villa_number: villaNumber } }),
  create: (data) => api.post('/payments', data),
  markPaid: (id) => api.patch(`/payments/${id}/pay`),
};

export const invoiceAPI = {
  getAll: (villaNumber, status) => api.get('/invoices', { params: { villa_number: villaNumber, status } }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  cancel: (id) => api.patch(`/invoices/${id}/cancel`),
  getUPILinks: (id) => api.get(`/invoices/${id}/upi-links`),
  confirmPayment: (id, paymentMethod, transactionId) => api.post(`/invoices/${id}/confirm-payment`, null, { 
    params: { payment_method: paymentMethod, transaction_id: transactionId } 
  }),
  uploadCSV: (formData) => api.post('/invoices/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const receiptAPI = {
  getAll: (villaNumber) => api.get('/receipts', { params: { villa_number: villaNumber } }),
  getOne: (id) => api.get(`/receipts/${id}`),
};

export const anprAPI = {
  getLatest: (limit = 10) => api.get('/anpr/latest', { params: { limit } }),
  getStatus: () => api.get('/anpr/status'),
  getHistory: (limit = 50) => api.get('/anpr/history', { params: { limit } }),
  recordGatePassVehicle: (gatePassId, vehiclePlate) => api.post('/anpr/gate-pass-entry', null, { params: { gate_pass_id: gatePassId, vehicle_plate: vehiclePlate } }),
  uploadGatePassVisitorPhoto: (gatePassId, file) => {
    const formData = new FormData();
    formData.append('gate_pass_id', gatePassId);
    formData.append('file', file);
    return api.post('/anpr/gate-pass-entry/photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const communityAPI = {
  getStats: () => api.get('/community/stats'),
  getUsers: () => api.get('/community/users'),
  createUser: (data) => api.post('/community/users', data),
  deleteUser: (id) => api.delete(`/community/users/${id}`),
  patchUser: (id, data) => api.patch(`/community/users/${id}`, data),
  uploadCSV: (formData) => api.post('/community/users/upload-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getJoinRequests: () => api.get('/community/join-requests'),
  approveJoinRequest: (id) => api.post(`/community/join-requests/${id}/approve`),
  rejectJoinRequest: (id) => api.post(`/community/join-requests/${id}/reject`),
  verifyPetVaccination: (userId, petId) => api.post(`/community/pets/${userId}/${petId}/verify-vaccination`),
  getPetVaccinationDocUrl: (userId, petId) => `${API_URL}/community/pets/${userId}/${petId}/vaccination-doc`,
  getMCMembers: () => api.get('/community/mc-members'),
  getDirectory: () => api.get('/community/directory'),
};

export const profileAPI = {
  get: () => api.get('/profile'),
  uploadPicture: (formData) => api.post('/profile/picture', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPictureUrl: (path) => `${API_URL}/profile/picture/${path}`,
  getPictureBlob: (path) => api.get(`/profile/picture/${path}`, { responseType: 'blob' }),
  getFamily: () => api.get('/profile/family'),
  addFamily: (name, phone) => api.post('/profile/family', null, { params: { name, phone } }),
  removeFamily: (id) => api.delete(`/profile/family/${id}`),
  getDailyHelp: () => api.get('/profile/daily-help'),
  searchStaff: (q) => api.get('/profile/staff-search', { params: { q } }),
  addDailyHelp: (staffId) => api.post('/profile/daily-help', null, { params: { staff_id: staffId } }),
  removeDailyHelp: (id) => api.delete(`/profile/daily-help/${id}`),
  getVehicles: () => api.get('/profile/vehicles'),
  addVehicle: (params) => api.post('/profile/vehicles', null, { params }),
  removeVehicle: (id) => api.delete(`/profile/vehicles/${id}`),
  setVehicleEPC: (id, epc_hex) => api.patch(`/profile/vehicles/${id}/epc`, null, { params: { epc_hex } }),
  uploadVehiclePicture: (vehicleId, formData) => api.post(`/profile/vehicles/${vehicleId}/picture`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getPets: () => api.get('/profile/pets'),
  addPet: (params) => api.post('/profile/pets', null, { params }),
  removePet: (id) => api.delete(`/profile/pets/${id}`),
  uploadVaccinationDoc: (petId, formData) => api.post(`/profile/pets/${petId}/vaccination-doc`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getVaccinationDocUrl: (petId) => `${API_URL}/profile/pets/${petId}/vaccination-doc`,
  getCommunities: () => api.get('/profile/communities'),
  searchCommunities: (q) => api.get('/profile/communities/search', { params: { q } }),
  requestCommunityJoin: (communityId) => api.post('/profile/communities/request', null, { params: { community_id: communityId } }),
  getHobbies: () => api.get('/profile/hobbies'),
  updateHobbies: (hobbies) => api.put('/profile/hobbies', hobbies),
  getPredefinedHobbies: () => api.get('/profile/hobbies/predefined'),
  getDirectoryPreference: () => api.get('/profile/directory-preference'),
  setDirectoryPreference: (params) => api.put('/profile/directory-preference', null, { params }),
};

export const blacklistAPI = {
  list: () => api.get('/management/blacklist'),
  add: (data) => api.post('/management/blacklist', data),
  remove: (entryId, source = 'manual') => api.delete(`/management/blacklist/${entryId}`, { params: { source } }),
  toggleVehicle: (vehicleId, isBlacklisted, reason) => api.post(`/management/vehicles/${vehicleId}/blacklist`, { is_blacklisted: isBlacklisted, reason: reason || '' }),
  check: (plate) => api.get('/anpr/blacklist-check', { params: { plate } }),
};

export const surveyAPI = {
  listForResident: () => api.get('/surveys'),
  listForManagement: () => api.get('/surveys/management'),
  create: (data) => api.post('/surveys', data),
  respond: (id, answers) => api.post(`/surveys/${id}/respond`, { answers }),
  results: (id) => api.get(`/surveys/${id}/results`),
  aggregated: (id) => api.get(`/surveys/${id}/aggregated`),
  updateExpiry: (id, expires_at) => api.patch(`/surveys/${id}/expiry`, { expires_at }),
  close: (id) => api.patch(`/surveys/${id}/close`),
  remove: (id) => api.delete(`/surveys/${id}`),
};

export const chatAPI = {
  getPreferences: () => api.get('/chat/preferences'),
  setPreferences: (allow) => api.put('/chat/preferences', { allow_direct_messages: allow }),
  messageableResidents: () => api.get('/chat/messageable-residents'),
  threads: () => api.get('/chat/threads'),
  createThread: (type, memberIds, name) => api.post('/chat/threads', { type, member_ids: memberIds, name }),
  messages: (threadId) => api.get(`/chat/threads/${threadId}/messages`),
  send: (threadId, body, imagePath) => api.post(`/chat/threads/${threadId}/messages`, { body, image_path: imagePath }),
  uploadImage: (threadId, formData) => api.post(`/chat/threads/${threadId}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  imageUrl: (messageId) => `${API_URL}/chat/messages/${messageId}/image`,
  markRead: (threadId) => api.patch(`/chat/threads/${threadId}/read`),
  leave: (threadId) => api.post(`/chat/threads/${threadId}/leave`),
  reportMessage: (messageId, reason, notes) => api.post(`/chat/messages/${messageId}/report`, { reason, notes }),
  listReports: () => api.get('/management/chat-reports'),
  resolveReport: (id) => api.patch(`/management/chat-reports/${id}`),
  listBlocks: () => api.get('/chat/blocks'),
  blockUser: (userId) => api.post('/chat/blocks', { user_id: userId }),
  unblockUser: (userId) => api.delete(`/chat/blocks/${userId}`),
};

export const recommendationAPI = {
  categories: () => api.get('/recommendations/categories'),
  list: (category) => api.get('/recommendations', { params: category ? { category } : {} }),
  create: (data) => api.post('/recommendations', data),
  remove: (id) => api.delete(`/recommendations/${id}`),
  rate: (id, stars, comment) => api.post(`/recommendations/${id}/rate`, { stars, comment }),
  ratings: (id) => api.get(`/recommendations/${id}/ratings`),
};

export const reportsAPI = {
  triggerMonthlyDigest: (force = false) => api.post('/management/reports/send-monthly-digest', null, { params: { force } }),
  triggerResidentRecaps: (force = false) => api.post('/management/reports/send-resident-recaps', null, { params: { force } }),
  triggerWeeklyPulse: (force = false) => api.post('/management/reports/send-weekly-pulse', null, { params: { force } }),
  log: () => api.get('/management/reports/log'),
  recapLog: () => api.get('/management/reports/recap-log'),
};

export const mcAdminAPI = {
  list: (communityId) => api.get(`/super-admin/communities/${communityId}/mc-members`),
  add: (communityId, data) => api.post(`/super-admin/communities/${communityId}/mc-members`, data),
  update: (communityId, memberId, data) => api.patch(`/super-admin/communities/${communityId}/mc-members/${memberId}`, data),
  remove: (communityId, memberId) => api.delete(`/super-admin/communities/${communityId}/mc-members/${memberId}`),
};

export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  getPreferences: () => api.get('/notification-preferences'),
  updatePreferences: (prefs) => api.put('/notification-preferences', prefs),
};

export const pushAPI = {
  subscribe: (subscription) => api.post('/notifications/subscribe', subscription),
  unsubscribe: () => api.delete('/notifications/unsubscribe'),
  getVapidKey: () => api.get('/notifications/vapid-key'),
};

export default api;